// Plik: src/components/IdentityClient.tsx
"use client";

import Link from "next/link";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

// --- KOMPONENTY POMOCNICZE (IKONY, GRIDY) ---

const BorderBeam = ({ duration = 10, delay = 0 }) => {
  return (
    <div className="absolute inset-0 rounded-xl pointer-events-none overflow-hidden">
        <motion.div
            className="absolute -inset-[100%]"
            animate={{ rotate: [0, 360] }}
            transition={{ duration: duration, repeat: Infinity, ease: "linear", delay: delay }}
            style={{
                background: "conic-gradient(from 0deg at 50% 50%, rgba(255,255,255,1) 0deg, rgba(255,255,255,0.3) 180deg, rgba(255,255,255,1) 360deg)",
                filter: "blur(8px)",
            }}
        />
        <div className="absolute inset-[1px] bg-inherit rounded-xl z-10" />
    </div>
  );
};

const GridPattern = () => (
  <div className="absolute inset-0 w-full h-full opacity-[0.04] pointer-events-none" 
       style={{ 
         backgroundImage: 'linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)', 
         backgroundSize: '40px 40px' 
       }}>
  </div>
);

const GridPatternDark = () => (
  <div className="absolute inset-0 w-full h-full opacity-[0.03] pointer-events-none" 
       style={{ 
         backgroundImage: 'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)', 
         backgroundSize: '40px 40px' 
       }}>
  </div>
);

// IKONY
const CheckIcon = () => (
  <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-600">
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M20 6L9 17l-5-5"/></svg>
  </div>
);

const XIcon = () => (
  <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-500">
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M18 6L6 18M6 6l12 12"/></svg>
  </div>
);

const FingerprintIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 12c0-3 2.5-5.5 5.5-5.5S23 9 23 12"/><path d="M12 12c0 3-2.5 5.5-5.5 5.5S1 15 1 12"/><path d="M12 12c0-3-2.5-5.5-5.5-5.5S1 9 1 12"/><path d="M12 12c0 3 2.5 5.5 5.5 5.5S23 15 23 12"/><path d="M12 2a10 10 0 0 1 10 10"/><path d="M12 22a10 10 0 0 1-10-10"/></svg>
);

const SearchIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
);

const PlusIcon = ({ isOpen }: { isOpen: boolean }) => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`transition-transform duration-300 ${isOpen ? "rotate-45" : ""}`}>
        <line x1="12" y1="5" x2="12" y2="19"></line>
        <line x1="5" y1="12" x2="19" y2="12"></line>
    </svg>
);

const FaqItem = ({ question, answer, isOpen, onClick }: { question: string, answer: string, isOpen: boolean, onClick: () => void }) => {
    return (
        <div className="border-b border-gray-200 last:border-0">
            <button onClick={onClick} className="w-full py-6 flex items-center justify-between text-left focus:outline-none group">
                <span className={`text-lg lg:text-xl font-bold transition-colors duration-300 pr-8 ${isOpen ? "text-blue-600" : "text-gray-900 group-hover:text-blue-600"}`}>{question}</span>
                <span className={`flex-shrink-0 flex items-center justify-center w-12 h-12 rounded-full border-2 transition-all duration-300 ${isOpen ? "bg-blue-600 border-blue-600 text-white rotate-45" : "bg-white border-gray-200 text-gray-500 group-hover:border-blue-600 group-hover:text-blue-600"}`}>
                    <PlusIcon isOpen={isOpen} />
                </span>
            </button>
            <AnimatePresence>
                {isOpen && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3, ease: "easeInOut" }} className="overflow-hidden">
                        <div className="pb-8 text-gray-800 leading-relaxed text-lg font-medium max-w-3xl">{answer}</div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default function IdentityClient() {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const toggleFaq = (index: number) => setOpenFaqIndex(openFaqIndex === index ? null : index);

  const faqData = [
    { 
        q: "Dlaczego potrzebuję własnej strony (wizytówki)?", 
        a: "Social media należą do Zuckerberga i Muska, a Twoja strona należy do Ciebie. To centrum dowodzenia, które Google indeksuje najwyżej. To tam masz pełną kontrolę nad narracją, ofertą i tym, jak postrzega Cię klient." 
    },
    { 
        q: "Po co mi optymalizacja Social Media, skoro tam nie działam?", 
        a: "Traktujemy Twoje profile (LinkedIn, Twitter, FB) jako 'satelity' SEO. Muszą być poprawnie skonfigurowane, aby zajmowały kolejne pozycje w Google pod Twoim nazwiskiem, spychając konkurencję lub przypadkowe wyniki na drugą stronę." 
    },
    { 
        q: "Jak wygląda proces budowy strony?", 
        a: "Tworzymy estetyczną, bardzo szybką stronę typu 'One Page' lub 'Personal Portfolio'. Jest zoptymalizowana technicznie (Schema.org), aby wyświetlała się w Google z rozszerzonymi wynikami (zdjęcie, linki pod spodem)." 
    },
    { 
        q: "Kiedy zobaczę zmiany w Google?", 
        a: "Indeksacja nowej strony i przetasowanie wyników zajmuje zazwyczaj od 2 do 6 tygodni. To proces budowania autorytetu domeny, ale efekt jest trwały i pracuje na Ciebie latami." 
    },
    { 
        q: "Czy muszę pisać artykuły?", 
        a: "W tym pakiecie skupiamy się na infrastrukturze (SEO Technical & On-Page). Treści są ważne, ale najważniejsze jest to, aby klient po wpisaniu Twojej firmy widział profesjonalną strukturę, a nie chaos." 
    }
  ];

  return (
    <main className="bg-[#F7F7F8] w-full min-h-screen relative selection:bg-blue-200 font-sans text-gray-900">
      

      {/* --- HERO SECTION --- */}
      <section className="relative pt-32 pb-12 lg:pt-48 lg:pb-12 px-6 overflow-hidden">
        <GridPattern />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[80vw] h-[50vh] bg-blue-500/5 blur-[120px] pointer-events-none rounded-full"></div>
        <div className="mx-auto max-w-[1250px] relative z-10">
            <div className="max-w-4xl mr-auto text-left">
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="inline-flex items-center gap-2 rounded-full bg-white border border-gray-200 px-3 py-1 text-xs font-bold text-blue-600 mb-6 shadow-sm mx-0">
                    <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span><span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span></span>
                    PAKIET TOŻSAMOŚCI CYFROWEJ
                </motion.div>
                
                <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-5xl lg:text-7xl font-bold tracking-tight text-gray-900 leading-[1.1] mb-8">
                    Przejmij kontrolę nad <br/>
                    <span className="text-blue-600">wynikami wyszukiwania.</span>
                </motion.h1>
                
                <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-0 font-medium">
                    Twój biznes działa od 5 lat i zarabia pieniądze, ale <strong>Twoja wizytówka w internecie wygląda jak nieudane CV sprzed 10 lat</strong>. Klienci sprawdzają Cię w Google, zanim odpiszą na maila. Czas, aby to, co widzą, budowało zaufanie, a nie wątpliwości.
                </motion.p>
                
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="mt-10 flex flex-wrap gap-4 justify-start">
                    <Link href="/kontakt" className="rounded-full bg-black px-8 py-4 text-sm font-bold text-white transition hover:bg-gray-800 shadow-xl hover:shadow-2xl hover:-translate-y-1">
                        Zbuduj wizerunek
                    </Link>
                    <Link href="#proces" className="rounded-full bg-white border border-gray-200 px-8 py-4 text-sm font-bold text-gray-900 transition hover:bg-gray-50 hover:border-gray-300">
                        Zobacz proces
                    </Link>
                </motion.div>
            </div>
        </div>
      </section>

      {/* --- PORÓWNANIE (PROBLEM VS ROZWIĄZANIE) --- */}
      <section className="pt-10 pb-20 lg:pt-0 lg:pb-32 px-6 relative">
        <GridPattern />
        <div className="mx-auto max-w-[1250px] relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-start lg:-mt-[10px]">
                
                {/* KARTA 1: PROBLEM (Duch w sieci) */}
                <div className="relative rounded-3xl bg-white border border-gray-200 p-8 lg:p-12 shadow-sm transition hover:shadow-md">
                    <div className="mb-8">
                        <div className="inline-block p-3 rounded-xl bg-red-50 text-red-500 mb-4">
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/></svg>
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">Chaos w wynikach</h3>
                        <p className="text-gray-500">Gdy ktoś wpisuje Twoje nazwisko, widzi przypadkowe profile, stare zdjęcia lub... konkurencję. Brak spójności to brak zaufania.</p>
                    </div>
                    <ul className="space-y-4">
                        <li className="flex items-start gap-3 text-gray-600">
                            <XIcon /> <span>Brak oficjalnej strony WWW</span>
                        </li>
                        <li className="flex items-start gap-3 text-gray-600">
                            <XIcon /> <span>Rozproszone informacje</span>
                        </li>
                        <li className="flex items-start gap-3 text-gray-600">
                            <XIcon /> <span>Brak kontroli nad tym, co widzi klient</span>
                        </li>
                        <li className="flex items-start gap-3 text-gray-600">
                            <XIcon /> <span>Wyglądasz na "jednego z wielu"</span>
                        </li>
                    </ul>
                </div>

                {/* KARTA 2: ROZWIĄZANIE (Lider Opinii) */}
                <div className="relative rounded-3xl bg-white border border-blue-200 p-8 lg:p-12 shadow-2xl shadow-blue-900/5 ring-4 ring-blue-50/50">
                    <div className="absolute top-0 right-0 bg-blue-600 text-white text-[10px] font-bold px-3 py-1.5 rounded-bl-xl rounded-tr-2xl uppercase tracking-wider">
                        Twój wybór
                    </div>
                    <div className="mb-8">
                        <div className="inline-block p-3 rounded-xl bg-blue-50 text-blue-600 mb-4">
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">Dominacja w SERP</h3>
                        <p className="text-gray-500">Tworzymy dla Ciebie profesjonalną stronę WWW zoptymalizowaną pod SEO. To ona jest centrum, a social media tylko ją wspierają.</p>
                    </div>
                    <ul className="space-y-4">
                        <li className="flex items-start gap-3 text-gray-800 font-medium">
                            <CheckIcon /> <span>Własna, szybka strona Wizytówka</span>
                        </li>
                        <li className="flex items-start gap-3 text-gray-800 font-medium">
                            <CheckIcon /> <span>SEO: Drzewko linków w Google</span>
                        </li>
                        <li className="flex items-start gap-3 text-gray-800 font-medium">
                            <CheckIcon /> <span>Skonfigurowane Social Media (Satelity)</span>
                        </li>
                        <li className="flex items-start gap-3 text-gray-800 font-medium">
                            <CheckIcon /> <span>Kontrola nad pierwszym wrażeniem</span>
                        </li>
                        <li className="flex items-start gap-3 text-gray-800 font-medium">
                            <CheckIcon /> <span>Pełna optymalizacja mobilna</span>
                        </li>
                    </ul>
                </div>

            </div>
        </div>
      </section>

      {/* --- CZYM JEST TOŻSAMOŚĆ? (CIEMNA, FULL SCREEN DESKTOP) --- */}
      <section id="features" className="bg-[#0b0c0e] py-16 lg:py-0 lg:min-h-screen flex flex-col justify-center relative overflow-hidden z-30 border-t border-white/5">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[60%] h-[60%] bg-blue-900/10 blur-[150px] pointer-events-none"></div>
        <div className="mx-auto max-w-[1250px] px-6 relative z-10 w-full">
          
          <div className="text-left lg:text-center max-w-3xl mx-0 lg:mx-auto mb-16">
             <h2 className="text-3xl lg:text-6xl font-bold tracking-tight text-white mb-6">
               Cyfrowa <br />
               <span className="text-gray-400">Twierdza.</span>
             </h2>
             <p className="text-sm lg:text-lg text-gray-500 max-w-2xl mx-0 lg:mx-auto">
               Nie polegamy na łasce algorytmów Facebooka. Budujemy Twoją własną infrastrukturę, która jest odporna na zmiany trendów i zawsze dostępna dla klienta.
             </p>
          </div>

          <div className="relative w-full max-w-4xl mx-auto mb-16 lg:mb-24">
             <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[110%] h-[110%] bg-gradient-to-r from-blue-600/20 to-purple-500/20 blur-[80px] rounded-full -z-10 opacity-60"></div>
             
             {/* CENTRALNY ELEMENT - WEBSITE & SATELLITES */}
             <div className="relative aspect-[16/9] lg:aspect-[21/9] w-full rounded-2xl overflow-hidden shadow-2xl bg-[#13151a] border border-white/5">
                 <BorderBeam duration={10} delay={0} />
                 <div className="absolute inset-[1px] bg-[#0b0c0e] rounded-2xl flex items-center justify-center overflow-hidden z-10">
                    <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20"></div>
                    
                    {/* WIZUALIZACJA: GŁÓWNA STRONA + SATELITY */}
                    <div className="relative flex flex-col items-center">
                        {/* Main Hub */}
                        <div className="w-48 h-32 bg-white/5 border border-white/10 rounded-xl mb-8 flex flex-col items-center justify-center relative z-20 shadow-[0_0_50px_rgba(37,99,235,0.2)]">
                            <div className="w-8 h-8 rounded-full bg-blue-500 mb-2"></div>
                            <div className="h-2 w-24 bg-gray-600 rounded"></div>
                            <div className="absolute -top-3 -right-3 bg-green-500 text-[#0b0c0e] text-[10px] font-bold px-2 py-0.5 rounded-full">WWW</div>
                        </div>

                        {/* Connections */}
                        <div className="absolute top-16 left-1/2 -translate-x-1/2 w-64 h-24 border-b border-l border-r border-white/10 rounded-b-3xl -z-10"></div>
                        <div className="absolute top-28 left-1/2 -translate-x-1/2 h-8 w-px bg-white/10"></div>

                        {/* Satellites */}
                        <div className="flex gap-4 mt-2">
                             <div className="flex flex-col items-center gap-2">
                                <div className="w-12 h-12 rounded-lg bg-[#0077b5]/10 border border-[#0077b5]/30 flex items-center justify-center text-[#0077b5]"><svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/></svg></div>
                                <span className="text-[10px] text-gray-500 uppercase tracking-widest">LinkedIN</span>
                             </div>
                             <div className="flex flex-col items-center gap-2">
                                <div className="w-12 h-12 rounded-lg bg-gray-700/30 border border-gray-600/30 flex items-center justify-center text-white"><svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg></div>
                                <span className="text-[10px] text-gray-500 uppercase tracking-widest">X / Twitter</span>
                             </div>
                             <div className="flex flex-col items-center gap-2">
                                <div className="w-12 h-12 rounded-lg bg-pink-500/10 border border-pink-500/30 flex items-center justify-center text-pink-500"><svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg></div>
                                <span className="text-[10px] text-gray-500 uppercase tracking-widest">IG / FB</span>
                             </div>
                        </div>
                    </div>
                 </div>
             </div>
          </div>

          {/* TRZY KOLUMNY CECH */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12 text-left max-w-5xl mx-auto">
             
             {/* Kolumna 1 */}
             <div className="space-y-4">
                <div className="w-12 h-12 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-500 border border-blue-500/20">
                   <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6"><path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" /></svg>
                </div>
                <h3 className="text-xl font-bold text-white">Własna Platforma</h3>
                <p className="text-gray-500 leading-relaxed text-sm">Tworzymy dla Ciebie profesjonalną stronę (One Page), która jest głównym wynikiem w Google. Ty ją kontrolujesz, nie algorytm.</p>
             </div>

             {/* Kolumna 2 */}
             <div className="space-y-4">
                <div className="w-12 h-12 rounded-lg bg-purple-500/10 flex items-center justify-center text-purple-500 border border-purple-500/20">
                   <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6"><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" /></svg>
                </div>
                <h3 className="text-xl font-bold text-white">Moc SEO</h3>
                <p className="text-gray-500 leading-relaxed text-sm">Optymalizujemy kod tak, aby strona wyświetlała się z "drzewkiem linków" (Sitelinks). To buduje ogromny autorytet w oczach klienta.</p>
             </div>

             {/* Kolumna 3 */}
             <div className="space-y-4">
                <div className="w-12 h-12 rounded-lg bg-green-500/10 flex items-center justify-center text-green-500 border border-green-500/20">
                   <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6"><path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" /></svg>
                </div>
                <h3 className="text-xl font-bold text-white">Ekosystem Linków</h3>
                <p className="text-gray-500 leading-relaxed text-sm">Konfigurujemy wszystkie social media, aby wyświetlały się pod Twoją stroną jako spójna lista wyników.</p>
             </div>

          </div>
        </div>
      </section>

      {/* --- DEEP DIVE: SOCIAL PROOF (JASNA, Sticky) --- */}
      <section className="bg-[#F7F7F8] py-16 lg:py-24 relative overflow-hidden border-t border-gray-200">
        <div className="mx-auto grid max-w-[1200px] grid-cols-1 gap-12 lg:gap-16 px-6 lg:grid-cols-[1fr_400px] items-start">
            
            {/* LEWA KOLUMNA (Treść) */}
            <div className="flex flex-col gap-8">
                <div className="relative">
                    <div className="inline-flex items-center gap-2 rounded-lg bg-blue-50 px-3 py-1 text-xs font-bold text-blue-700 mb-4">
                        <FingerprintIcon /> ZAUFANIE W BIZNESIE
                    </div>
                    <h2 className="text-4xl font-bold tracking-tight text-gray-900 md:text-5xl leading-[1.15] mb-6">
                        Ludzie kupują od ludzi, <br />
                        <span className="text-blue-600">którzy istnieją w Google.</span>
                    </h2>
                    <p className="text-lg text-gray-600 leading-relaxed">
                        Jeśli nie ma Cię w Google, dla wielu klientów nie istniejesz. A jeśli tam jesteś, ale Twoje wyniki to chaos – tracisz wiarygodność. Naprawiamy to.
                    </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-4">
                    <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center text-green-600 mb-4 font-bold text-xl">👀</div>
                        <h4 className="font-bold text-gray-900 mb-2">Widoczność</h4>
                        <p className="text-sm text-gray-500">Pierwszy wynik w Google musi być Twój. Bez wyjątków.</p>
                    </div>
                    <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                        <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center text-purple-600 mb-4 font-bold text-xl">💎</div>
                        <h4 className="font-bold text-gray-900 mb-2">Prestiż</h4>
                        <p className="text-sm text-gray-500">Profesjonalna strona buduje autorytet jeszcze przed pierwszym spotkaniem.</p>
                    </div>
                    <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                        <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center text-orange-600 mb-4 font-bold text-xl">⚡</div>
                        <h4 className="font-bold text-gray-900 mb-2">Szybkość</h4>
                        <p className="text-sm text-gray-500">Twoja strona ładuje się błyskawicznie. Zero czekania, zero irytacji.</p>
                    </div>
                    <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 mb-4 font-bold text-xl">🔗</div>
                        <h4 className="font-bold text-gray-900 mb-2">Porządek</h4>
                        <p className="text-sm text-gray-500">Wszystkie linki (LinkedIn, FB, artykuły) w jednym, uporządkowanym miejscu.</p>
                    </div>
                </div>
            </div>

            {/* PRAWA KOLUMNA (Visual) */}
            <div className="relative mt-8 lg:mt-0 hidden lg:block">
                <div className="sticky top-32 rounded-xl bg-white p-8 shadow-[0_20px_50px_rgba(0,0,0,0.1)] border border-gray-200 h-[400px] relative overflow-hidden flex items-center justify-center">
                    <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: 'radial-gradient(#000 1px, transparent 1px)', backgroundSize: '20px 20px' }}></div>
                    
                    {/* Element graficzny: Wynik Wyszukiwania */}
                    <div className="w-full max-w-xs bg-white rounded-lg border border-gray-200 p-4 shadow-lg scale-110">
                        {/* Search Bar Mockup */}
                        <div className="flex items-center gap-3 bg-gray-100 rounded-full px-4 py-2 mb-6 opacity-70">
                            <SearchIcon />
                            <div className="h-2 w-32 bg-gray-300 rounded"></div>
                        </div>

                        {/* Result 1 (Main) */}
                        <div className="mb-4">
                            <div className="flex items-center gap-2 mb-1">
                                <div className="w-5 h-5 rounded-full bg-blue-500"></div>
                                <div className="h-2 w-24 bg-gray-800 rounded"></div>
                            </div>
                            <div className="h-3 w-48 bg-blue-600 rounded mb-2"></div>
                            <div className="space-y-1">
                                <div className="h-2 w-full bg-gray-200 rounded"></div>
                                <div className="h-2 w-5/6 bg-gray-200 rounded"></div>
                            </div>
                        </div>

                        {/* Sitelinks (Drzewko) */}
                        <div className="grid grid-cols-2 gap-3 ml-4">
                            <div>
                                <div className="h-2 w-16 bg-blue-600 rounded mb-1"></div>
                                <div className="h-1.5 w-24 bg-gray-300 rounded"></div>
                            </div>
                            <div>
                                <div className="h-2 w-16 bg-blue-600 rounded mb-1"></div>
                                <div className="h-1.5 w-24 bg-gray-300 rounded"></div>
                            </div>
                            <div>
                                <div className="h-2 w-16 bg-blue-600 rounded mb-1"></div>
                                <div className="h-1.5 w-24 bg-gray-300 rounded"></div>
                            </div>
                            <div>
                                <div className="h-2 w-16 bg-blue-600 rounded mb-1"></div>
                                <div className="h-1.5 w-24 bg-gray-300 rounded"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
      </section>

      {/* --- NOWOŚĆ: FILOZOFIA WIZERUNKU (SEKCJA DODANA) --- */}
      <section className="bg-white py-20 lg:py-32 px-6 border-t border-gray-200">
        <div className="mx-auto max-w-[1250px]">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center">
                
                {/* LEWA KOLUMNA: TREŚĆ */}
                <div>
                    <span className="text-blue-600 font-bold tracking-widest uppercase text-xs mb-4 block">Filozofia Wizerunku</span>
                    <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-6">Inżynieria Wizerunku.</h2>
                    <div className="prose prose-lg text-gray-600">
                        <p>
                            Większość ludzi myśli, że wizerunek w sieci to kwestia przypadku. Że Google "samo" decyduje, co pokazać. <strong>To błąd.</strong>
                        </p>
                        <p className="mt-4">
                            Google to robot. Karmi się danymi strukturalnymi. Jeśli dostarczysz mu idealnie uporządkowaną paczkę danych: stronę, mapę witryny, połączone profile social media – nagrodzi Cię czytelnym, dominującym wynikiem.
                        </p>
                        <p className="mt-4 font-medium text-gray-900">
                            Nie zostawiamy miejsca na domysły. Projektujemy Twoją obecność w sieci tak, jak architekt projektuje budynek. Klient ma widzieć to, co chcemy mu pokazać: profesjonalizm, ład i kompetencje.
                        </p>
                    </div>
                </div>

                {/* PRAWA KOLUMNA: WIZUALIZACJA (CHAOS vs STRUKTURA) */}
                <div className="relative">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        
                        {/* Karta 1: CHAOS (Typowy Profil) */}
                        <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 opacity-60 scale-95 blur-[1px]">
                            <div className="flex items-center gap-2 mb-4">
                                <div className="w-2 h-2 rounded-full bg-red-400"></div>
                                <span className="text-xs font-bold text-red-400 uppercase">Typowy Wynik (Chaos)</span>
                            </div>
                            <div className="space-y-4">
                                {/* Random Result 1 */}
                                <div>
                                    <div className="h-3 w-1/2 bg-blue-300/50 rounded mb-1"></div>
                                    <div className="h-2 w-full bg-gray-200 rounded"></div>
                                </div>
                                {/* Random Result 2 */}
                                <div>
                                    <div className="h-3 w-1/3 bg-blue-300/50 rounded mb-1"></div>
                                    <div className="h-2 w-3/4 bg-gray-200 rounded"></div>
                                </div>
                                {/* Image Noise */}
                                <div className="flex gap-2 opacity-50">
                                    <div className="w-8 h-8 bg-gray-300 rounded"></div>
                                    <div className="w-8 h-8 bg-gray-300 rounded"></div>
                                </div>
                            </div>
                        </div>

                        {/* Karta 2: STRUKTURA (WEBPOL) */}
                        <div className="bg-white border border-blue-100 rounded-xl p-6 shadow-xl shadow-blue-100 relative md:-ml-12 md:mt-12 ring-1 ring-blue-50">
                            <div className="absolute top-0 right-0 bg-blue-600 text-white text-[10px] font-bold px-2 py-1 rounded-bl-lg rounded-tr-lg">STRATEGIA</div>
                            <div className="flex items-center gap-2 mb-6">
                                <div className="w-2 h-2 rounded-full bg-green-500"></div>
                                <span className="text-xs font-bold text-gray-900 uppercase">Wynik Webpol</span>
                            </div>
                            <div className="space-y-3">
                                {/* Main Site */}
                                <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                                    <div className="h-3 w-3/4 bg-blue-600 rounded mb-2"></div>
                                    <div className="h-2 w-full bg-blue-200 rounded"></div>
                                </div>
                                {/* Sitelinks Grid */}
                                <div className="grid grid-cols-2 gap-2">
                                    <div className="h-2 bg-gray-100 rounded"></div>
                                    <div className="h-2 bg-gray-100 rounded"></div>
                                    <div className="h-2 bg-gray-100 rounded"></div>
                                    <div className="h-2 bg-gray-100 rounded"></div>
                                </div>
                                {/* Social Proof Row */}
                                <div className="flex gap-2 mt-2 pt-2 border-t border-gray-100">
                                    <div className="w-4 h-4 rounded bg-gray-200"></div>
                                    <div className="w-4 h-4 rounded bg-gray-200"></div>
                                    <div className="w-4 h-4 rounded bg-gray-200"></div>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>

            </div>
        </div>
      </section>

      {/* --- PROCES WSPÓŁPRACY (CIEMNA, FULL SCREEN DESKTOP) --- */}
      <section id="proces" className="py-24 px-6 relative overflow-hidden bg-[#0b0c0e] border-t border-white/5 lg:py-0 lg:min-h-screen flex flex-col justify-center">
        <GridPatternDark />
        <div className="mx-auto max-w-[1000px] relative z-10 w-full">
            <div className="mb-16 text-left lg:text-center">
                <h2 className="text-3xl lg:text-5xl font-bold text-white mb-6">Jak przejmujemy kontrolę?</h2>
                <p className="text-xl text-gray-400 max-w-2xl mx-0 lg:mx-auto">Cztery kroki do uporządkowania Twojego cyfrowego świata.</p>
            </div>

            <div className="relative">
                <div className="absolute left-4 lg:left-1/2 top-0 bottom-0 w-px bg-white/10 -z-10"></div>
                <div className="space-y-12 lg:space-y-0">
                    
                    {/* ETAP 1 */}
                    <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between group lg:mb-12">
                        <div className="w-full pl-12 lg:w-[45%] lg:pl-0 lg:pr-12 lg:text-right order-2 lg:order-1 mt-2 lg:mt-0">
                            <span className="inline-block px-2 py-1 bg-blue-500/10 text-blue-400 text-[10px] font-bold uppercase tracking-wider rounded mb-2">Etap 01</span>
                            <h3 className="text-xl font-bold text-white mb-2">Audyt Widoczności</h3>
                            <p className="text-gray-400 text-sm leading-relaxed">Sprawdzamy co Google "myśli" o Tobie teraz. Szukamy błędnych informacji, starych profili i konkurencji, która Cię wyprzedza.</p>
                        </div>
                        <div className="absolute left-4 lg:left-1/2 w-8 h-8 lg:w-10 lg:h-10 -translate-x-1/2 bg-[#0b0c0e] border-4 border-blue-500 rounded-full flex items-center justify-center z-10 shadow-sm order-1">
                            <span className="text-[10px] lg:text-xs font-bold text-blue-500">1</span>
                        </div>
                        <div className="w-full pl-12 lg:w-[45%] lg:pl-12 order-3 mt-4 lg:mt-0">
                             <ul className="text-xs text-gray-400 space-y-1 bg-white/5 p-4 rounded-xl border border-white/5 inline-block w-full">
                                <li className="flex items-center gap-2">🔹 Analiza wyników (SERP)</li>
                                <li className="flex items-center gap-2">🔹 Weryfikacja domeny</li>
                                <li className="flex items-center gap-2">🔹 Plan czyszczenia</li>
                             </ul>
                        </div>
                    </div>

                    {/* ETAP 2 */}
                    <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between group lg:mb-12">
                        <div className="w-full pl-12 lg:w-[45%] lg:pl-0 lg:pr-12 lg:text-right order-3 lg:order-1 mt-4 lg:mt-0">
                             <ul className="lg:hidden text-xs text-gray-400 space-y-1 bg-white/5 p-4 rounded-xl border border-white/5 inline-block w-full">
                                <li className="flex items-center gap-2">🔹 Projekt graficzny</li>
                                <li className="flex items-center gap-2">🔹 Copywriting sprzedażowy</li>
                                <li className="flex items-center gap-2">🔹 Optymalizacja techniczna</li>
                             </ul>
                             <ul className="hidden lg:inline-block w-full text-xs text-gray-400 space-y-1 bg-white/5 p-4 rounded-xl border border-white/5">
                                <li className="flex items-center gap-2 justify-end">Projekt graficzny 🔹</li>
                                <li className="flex items-center gap-2 justify-end">Copywriting sprzedażowy 🔹</li>
                                <li className="flex items-center gap-2 justify-end">Optymalizacja techniczna 🔹</li>
                             </ul>
                        </div>
                        <div className="absolute left-4 lg:left-1/2 w-8 h-8 lg:w-10 lg:h-10 -translate-x-1/2 bg-[#0b0c0e] border-4 border-purple-500 rounded-full flex items-center justify-center z-10 shadow-sm order-1">
                            <span className="text-[10px] lg:text-xs font-bold text-purple-500">2</span>
                        </div>
                        <div className="w-full pl-12 lg:w-[45%] lg:pl-12 order-2 lg:order-3 mt-2 lg:mt-0">
                            <span className="inline-block px-2 py-1 bg-purple-500/10 text-purple-400 text-[10px] font-bold uppercase tracking-wider rounded mb-2">Etap 02</span>
                            <h3 className="text-xl font-bold text-white mb-2">Budowa Twierdzy (Strona WWW)</h3>
                            <p className="text-gray-400 text-sm leading-relaxed">Tworzymy i uruchamiamy Twoją profesjonalną stronę. To ona będzie kotwicą dla całego ruchu z Google.</p>
                        </div>
                    </div>

                    {/* ETAP 3 */}
                    <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between group lg:mb-12">
                        <div className="w-full pl-12 lg:w-[45%] lg:pl-0 lg:pr-12 lg:text-right order-2 lg:order-1 mt-2 lg:mt-0">
                            <span className="inline-block px-2 py-1 bg-orange-500/10 text-orange-400 text-[10px] font-bold uppercase tracking-wider rounded mb-2">Etap 03</span>
                            <h3 className="text-xl font-bold text-white mb-2">Konfiguracja Satelitów</h3>
                            <p className="text-gray-400 text-sm leading-relaxed">Zakładamy i konfigurujemy profile w Social Media. Nie musisz tam postować – one mają po prostu "być" i linkować do strony.</p>
                        </div>
                        <div className="absolute left-4 lg:left-1/2 w-8 h-8 lg:w-10 lg:h-10 -translate-x-1/2 bg-[#0b0c0e] border-4 border-orange-500 rounded-full flex items-center justify-center z-10 shadow-sm order-1">
                            <span className="text-[10px] lg:text-xs font-bold text-orange-500">3</span>
                        </div>
                        <div className="w-full pl-12 lg:w-[45%] lg:pl-12 order-3 mt-4 lg:mt-0">
                             <ul className="text-xs text-gray-400 space-y-1 bg-white/5 p-4 rounded-xl border border-white/5 inline-block w-full">
                                <li className="flex items-center gap-2">🔹 LinkedIn / Twitter / FB</li>
                                <li className="flex items-center gap-2">🔹 Spójny branding (Logo/Tło)</li>
                                <li className="flex items-center gap-2">🔹 Cross-linkowanie</li>
                             </ul>
                        </div>
                    </div>

                    {/* ETAP 4 */}
                    <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between group">
                        <div className="w-full pl-12 lg:w-[45%] lg:pl-0 lg:pr-12 lg:text-right order-3 lg:order-1 mt-4 lg:mt-0">
                             <ul className="lg:hidden text-xs text-gray-400 space-y-1 bg-white/5 p-4 rounded-xl border border-white/5 inline-block w-full">
                                <li className="flex items-center gap-2">🔹 Zgłoszenie do Google</li>
                                <li className="flex items-center gap-2">🔹 Indeksacja linków</li>
                                <li className="flex items-center gap-2">🔹 Monitoring pozycji</li>
                             </ul>
                             <ul className="hidden lg:inline-block w-full text-xs text-gray-400 space-y-1 bg-white/5 p-4 rounded-xl border border-white/5">
                                <li className="flex items-center gap-2 justify-end">Zgłoszenie do Google 🔹</li>
                                <li className="flex items-center gap-2 justify-end">Indeksacja linków 🔹</li>
                                <li className="flex items-center gap-2 justify-end">Monitoring pozycji 🔹</li>
                             </ul>
                        </div>
                        <div className="absolute left-4 lg:left-1/2 w-8 h-8 lg:w-10 lg:h-10 -translate-x-1/2 bg-[#0b0c0e] border-4 border-green-500 rounded-full flex items-center justify-center z-10 shadow-sm order-1">
                            <span className="text-[10px] lg:text-xs font-bold text-green-500">4</span>
                        </div>
                        <div className="w-full pl-12 lg:w-[45%] lg:pl-12 order-2 lg:order-3 mt-2 lg:mt-0">
                            <span className="inline-block px-2 py-1 bg-green-500/10 text-green-400 text-[10px] font-bold uppercase tracking-wider rounded mb-2">Etap 04</span>
                            <h3 className="text-xl font-bold text-white mb-2">Indeksacja & Efekt</h3>
                            <p className="text-gray-400 text-sm leading-relaxed">Zgłaszamy mapę witryny do Google. Po kilku tygodniach Twoje wyniki wyszukiwania zaczynają wyglądać profesjonalnie.</p>
                        </div>
                    </div>

                </div>
            </div>
        </div>
      </section>

      {/* --- FAQ (ACCORDION) --- */}
      <section className="py-24 px-6 bg-white border-t border-gray-100 relative">
        <GridPattern />
        <div className="mx-auto max-w-3xl relative z-10">
            <div className="text-left lg:text-center mb-16">
                <span className="text-blue-600 font-bold tracking-widest uppercase text-xs mb-3 block">Wiedza</span>
                <h2 className="text-3xl font-bold text-gray-900">Najczęstsze pytania</h2>
            </div>
            
            <div className="space-y-4">
                {faqData.map((item, index) => (
                    <FaqItem 
                        key={index} 
                        question={item.q} 
                        answer={item.a} 
                        isOpen={openFaqIndex === index}
                        onClick={() => toggleFaq(index)}
                    />
                ))}
            </div>
        </div>
      </section>

      {/* --- CTA --- */}
      <section className="py-32 px-6 relative overflow-hidden">
        <GridPattern />
        <div className="mx-auto max-w-4xl text-center relative z-10">
            <div className="inline-block p-4 rounded-full bg-blue-100 text-blue-600 mb-6">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>
            </div>
            <h2 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6">Przestań być anonimowy.</h2>
            <p className="text-xl text-gray-600 mb-10 max-w-2xl mx-auto">
                Zbuduj wizerunek eksperta, który przyciąga klientów, zamiast za nimi gonić.
            </p>
            <Link href="/kontakt" className="inline-block rounded-full bg-black px-10 py-5 text-lg font-bold text-white shadow-xl hover:scale-105 hover:bg-gray-800 transition-all">
                Umów konsultację
            </Link>
        </div>
      </section>

    </main>
  );
}